import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    IconButton,
    CircularProgress,
    Paper,
    Divider,
} from '@mui/material';
import {
    PhotoCamera,
    Delete,
    Image as ImageIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';

const DJChatBackgroundManagement = () => {
    const { user, updateUser } = useAuth();
    const queryClient = useQueryClient();
    const [loading, setLoading] = useState({});

    const uploadMutation = useMutation({
        mutationFn: ({ index, formData }) => userService.uploadChatBackground(index, formData),
        onSuccess: async (response, { index }) => {
            toast.success(`Background ${index} atualizado com sucesso!`);
            try {
                const meRes = await authService.getMe();
                if (meRes.data.user) {
                    updateUser(meRes.data.user);
                }
            } catch (err) {
                console.error('Falha ao sincronizar usuário:', err);
            }
            queryClient.invalidateQueries(['currentUser']);
            queryClient.invalidateQueries(['user']);
            queryClient.invalidateQueries(['liveProgram']);
            setLoading(prev => ({ ...prev, [index]: false }));
        },
        onError: (error, { index }) => {
            toast.error(error.response?.data?.error || 'Erro ao carregar background');
            setLoading(prev => ({ ...prev, [index]: false }));
        }
    });

    const removeMutation = useMutation({
        mutationFn: (index) => userService.removeChatBackground(index),
        onSuccess: async (response, index) => {
            toast.success(`Background ${index} removido com sucesso!`);
            try {
                const meRes = await authService.getMe();
                if (meRes.data.user) {
                    updateUser(meRes.data.user);
                }
            } catch (err) {
                console.error('Falha ao sincronizar usuário:', err);
            }
            queryClient.invalidateQueries(['currentUser']);
            queryClient.invalidateQueries(['user']);
            queryClient.invalidateQueries(['liveProgram']);
            setLoading(prev => ({ ...prev, [index]: false }));
        },
        onError: (error, { index }) => {
            toast.error(error.response?.data?.error || 'Erro ao remover background');
            setLoading(prev => ({ ...prev, [index]: false }));
        }
    });

    const handleFileChange = (index, event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Arquivo muito grande (máximo 5MB)');
            return;
        }

        const formData = new FormData();
        formData.append('chat_bg', file);

        setLoading(prev => ({ ...prev, [index]: true }));
        uploadMutation.mutate({ index, formData });
    };

    const handleRemove = (index) => {
        if (window.confirm(`Deseja remover o background ${index}?`)) {
            setLoading(prev => ({ ...prev, [index]: true }));
            removeMutation.mutate(index);
        }
    };

    const renderBgSlot = (index) => {
        const fieldName = `chat_bg_${index}`;
        const imageUrl = user[fieldName];

        return (
            <Grid item xs={12} md={4} key={index}>
                <Paper
                    elevation={4}
                    sx={{
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 2,
                        position: 'relative',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ color: '#F59E0B', fontWeight: 700, mb: 2 }}>
                        Background {index}
                    </Typography>

                    <Box
                        sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            border: '1px dashed rgba(255, 255, 255, 0.2)',
                            position: 'relative'
                        }}
                    >
                        {loading[index] ? (
                            <CircularProgress color="warning" />
                        ) : imageUrl ? (
                            <>
                                <img
                                    src={imageUrl}
                                    alt={`Background ${index}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(rgba(0,0,0,0) 60%, rgba(0,0,0,0.3))'
                                    }}
                                />
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', opacity: 0.5 }}>
                                <ImageIcon sx={{ fontSize: 48, mb: 1 }} />
                                <Typography variant="caption" display="block">Vazio</Typography>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button
                            component="label"
                            variant="contained"
                            fullWidth
                            startIcon={<PhotoCamera />}
                            disabled={loading[index]}
                            sx={{
                                bgcolor: '#8B5CF6',
                                '&:hover': { bgcolor: '#7C3AED' }
                            }}
                        >
                            Upload
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileChange(index, e)}
                            />
                        </Button>
                        {imageUrl && (
                            <IconButton
                                color="error"
                                onClick={() => handleRemove(index)}
                                disabled={loading[index]}
                                sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)' }}
                            >
                                <Delete />
                            </IconButton>
                        )}
                    </Box>
                </Paper>
            </Grid>
        );
    };

    return (
        <Box>
            <Card sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box mb={3}>
                        <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#F59E0B', mb: 1 }}>
                            Backgrounds do Chat
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Cadastre até 3 imagens de fundo para o chat. Quando você estiver ao vivo, poderá alternar entre elas a cada 15 minutos automaticamente.
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                    <Grid container spacing={3}>
                        {[1, 2, 3].map(index => renderBgSlot(index))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default DJChatBackgroundManagement;
